/**
 * Maps URL paths to appropriate page titles for Event API
 * Works for both localhost and remote environments
 * @returns Formatted page title string
 */
 // Structure for mapping path patterns to titles
  interface PageTitleMapping {
    pattern: RegExp;
    title: string;
    type: string;
  }

  interface PageTitleResponse {
    title: string;
    type: string;
  }

export const transformTitleTypeAndMapUrl = (): PageTitleResponse => {
  const url = window.location.href;
  const pathname = window.location.pathname;

  // Extract application type from URL
  let appType: string;
  let pathSegments: string[];

  if (url.includes("localhost") || url.includes("127.0.0.1")) {
    const NotSelfcareAppType = ["retail","ps","military"];
    // Local development environment
    pathSegments = pathname.split("/").filter(Boolean);
    appType = pathSegments[0] || "";
    // Remove appType from segments for consistent processing
    if (!NotSelfcareAppType.includes(appType)){
      appType = "my";
    }
    else {
      pathSegments = pathSegments.slice(1);
    }
    
  } else {
    // Remote environment - extract from subdomain
    const hostnameSegments = window.location.hostname.split(".");
    // Handle formats like 'retail-dev' or just 'retail'
    appType = hostnameSegments[0]?.split("-")[0] || "";
    pathSegments = pathname.split("/").filter(Boolean);
  }

  // Normalize appType to lowercase for comparison
  const appTypeLower = appType.toLowerCase();

 

  // Define mappings for each application type
  const mappings: Record<string, PageTitleMapping[]> = {
    retail: [
      { pattern: /^$|^\/$/, title: "Boingo Retail - Landing" , type:"landing"}, // Landing page
      { pattern: /^[^\/]+$/, title: "Boingo Retail - Landing", type:"landing" }, // Location only (/lax)
      { pattern: /^[^\/]+\/signup$/, title: "Boingo Retail - Sign Up", type:"signup" },
      { pattern: /^[^\/]+\/signin$/, title: "Boingo Retail - Sign In", type:"signin" },
      { pattern: /^[^\/]+\/welcome$/, title: "Boingo Retail - Welcome", type:"welcome" }, // Signup page
      { pattern: /^[^\/]+\/error$/, title: "Boingo Retail - Error", type:"error"}, // Error page
      { pattern: /^[^\/]+\/c9_login$/, title: "Boingo Retail - C9 Login", type:"c9_login"}, // Error page
      // Add more retail patterns as needed
    ],
    military: [
      { pattern: /^$|^\/$/, title: "Boingo Military - Internet Sales", type:"landing" }, // Landing page
      { pattern: /^[^\/]+$/, title: "Boingo Military - Internet Sales", type:"landing" }, // Location only (e.g., /mccpen)
      {
        pattern: /^[^\/]+\/additional-devices$/,
        title: "Boingo Military - Additional Devices",
        type:"devices"
      },
      { pattern: /^[^\/]+\/checkout$/, title: "Boingo Military - Checkout", type: "checkout" }, // Checkout page
      { pattern: /^[^\/]+\/welcome$/, title: "Boingo Military - Welcome", type:"welcome" }, // Welcome page
      { pattern: /^[^\/]+\/error$/, title: "Boingo Military - Error", type:"error" }, // Error page
      // Default pattern should be last to catch any unspecified routes
    ],
    ps: [
      { pattern: /^$|^\/$/, title: "Boingo Private-Services - Landing", type:"landing" }, // Landing page
      { pattern: /^[^\/]+$/, title: "Boingo Private-Services - Landing", type:"landing" }, // Location/Service only (e.g., /army)
      {
        pattern: /^[^\/]+\/welcome$/,
        title: "Boingo Private-Services - Welcome",
        type:"welcome"
      }, // Welcome page (e.g., /army/welcome)
      { pattern: /^[^\/]+\/error$/, title: "Boingo Private-Services - Error", type:"error" }, // Error page
      // Add more private services patterns as needed
    ],
    my: [
       { pattern: /^login$/, title: "Boingo Selfcare - Login", type:"login" },
       { pattern: /^account-summary$/, title: "Boingo Selfcare - Account Summary", type:"account_summary" },
       { pattern: /^my-info$/, title: "Boingo Selfcare - My Info", type:"my_info" },
       { pattern: /^my-devices$/, title: "Boingo Selfcare - My Devices", type:"my_devices" },
       { pattern: /^my-plan$/, title: "Boingo Selfcare - My Plan", type:"my_plan" },
       { pattern: /^my-billing$/, title: "Boingo Selfcare - My Billing", type:"my_billing" },
       { pattern: /^my-charges$/, title: "Boingo Selfcare - My Charges", type:"my_charges" },
       { pattern: /^cancel-plan$/, title: "Boingo Selfcare - Cancel Plan", type:"cancel_plan" },
       { pattern: /^change-plan\/[^/]+\/checkout$/, title: "Boingo Selfcare - Change Plan Checkout", type:"change_plan_checkout" },
       { pattern: /^change-plan\/[^/]+\/additional-devices$/, title: "Boingo Selfcare - Change Plan Devices", type:"change_plan_devices" },
       { pattern: /^change-plan\/.+/, title: "Boingo Selfcare - Change Plan", type:"change_plan_landing" },
    ]
  };

  // Check if we have mappings for this app type
  if (mappings[appTypeLower]) {
    // Join path segments for pattern matching
    let pathForMatching = pathSegments.join("/");
    // Find the first matching pattern
    const match = mappings[appTypeLower].find((mapping) =>{
      return mapping.pattern.test(pathForMatching)
    }
    );
    if (match) {
      return match;
    }
  }

  // Special case for specific first-level URLs with no mapping (like "my-devices")
  // This avoids breaking the app type into "My" and trying to concatenate with the path
  if (pathname.startsWith("/") && pathname.slice(1).includes("-")) {
    const segment = pathname.slice(1); // Remove leading slash
    // Capitalize each word in the segment
    const formattedSegment = segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("-");

    return {title: `Boingo ${formattedSegment}`,type:`${window.location.pathname}`};
  }

  // Default fallback - generate a sensible title from URL components
  const formattedAppType =
    appTypeLower.charAt(0).toUpperCase() + appTypeLower.slice(1);

  // If we have path segments, use the last one as a page indicator
  if (pathSegments.length > 0) {
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Capitalize each word in the segment, including after hyphens
    const formattedSegment = lastSegment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("-");

    return { title: `Boingo ${formattedAppType} - ${formattedSegment}`, type:`${window.location.pathname}` }; //pathname for troubleshooting, we shouldn't get here
  }

  // Most generic fallback
  return {title: `Boingo ${formattedAppType}`, type:`${window.location.pathname}`}; //pathname for troubleshooting, we should never get here.
};

export default transformTitleTypeAndMapUrl;
